import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowUp, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EXAMPLE_QUESTIONS, MORE_QUESTIONS } from "@/data/examples";
import { API, streamAsk } from "@/lib/api";
import { parseAnswer } from "@/lib/parseAnswer";
import AnswerView from "@/components/AnswerView";
import Gallery from "@/components/Gallery";
import DailyVerse from "@/components/DailyVerse";
import Journeys from "@/components/Journeys";
import Favorites from "@/components/Favorites";
import { CrossBar } from "@/components/Ornaments";
import SmartImage from "@/components/SmartImage";

export default function BibleAnswers() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("idle"); // idle | streaming | answered
  const [answer, setAnswer] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const idRef = useRef(null);

  const ask = useCallback(async (text) => {
    const q = (text ?? "").trim();
    if (!q) return;
    setQuery(q);
    setStatus("streaming");
    setImageLoading(false);
    setAnswer({ question: q, title: "", intro: "", story: "", verses: [], reflection: "" });
    idRef.current = null;
    window.scrollTo({ top: 0, behavior: "smooth" });

    try {
      await streamAsk(q, {
        onMeta: (id) => { idRef.current = id; },
        onBuffer: (full) => {
          const parsed = parseAnswer(full);
          setAnswer((prev) => ({ ...prev, ...parsed, question: q }));
        },
        onError: () => {
          toast.error("The answer could not be revealed. Please try again.");
          setStatus("idle");
        },
        onDone: async (finalAnswer) => {
          setAnswer(finalAnswer);
          setStatus("answered");
          setImageLoading(true);
          try {
            const { data } = await axios.post(`${API}/answer/${finalAnswer.id}/illustration`);
            setAnswer(data);
          } catch {
            toast.message("The words are ready; the illustration could not be painted this time.");
          } finally {
            setImageLoading(false);
          }
        },
      });
    } catch (e) {
      console.error(e);
      toast.error("The answer could not be revealed. Please try again.");
      setStatus("idle");
    }
  }, []);

  const reset = () => {
    setStatus("idle");
    setAnswer(null);
    setImageLoading(false);
    setQuery("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const compact = status !== "idle";

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div className="paper-grain pointer-events-none fixed inset-0 z-0 opacity-[0.15]" />

      <CrossBar />

      <AnimatePresence>
        {compact && (
          <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -80, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="sticky top-0 z-30 border-b border-border/70 bg-background/85 backdrop-blur-md"
          >
            <div className="mx-auto flex max-w-6xl items-center gap-4 px-6 py-4">
              <button
                data-testid="home-button"
                onClick={reset}
                className="flex shrink-0 items-center gap-2.5 font-serif text-2xl tracking-tight text-primary transition-opacity hover:opacity-70"
              >
                <img src="/logo.png" alt="AskYeshua" className="h-8 w-8" />
                AskYeshua
              </button>
              <form
                onSubmit={(e) => { e.preventDefault(); ask(query); }}
                className="ml-auto flex w-full max-w-md items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 shadow-sm transition-colors focus-within:border-primary"
              >
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="search-input-compact"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ask another question..."
                  className="w-full bg-transparent font-sans text-sm outline-none placeholder:text-muted-foreground"
                />
                <Button data-testid="ask-button-compact" type="submit" size="icon" className="h-8 w-8 shrink-0 rounded-full">
                  <ArrowUp className="h-4 w-4" />
                </Button>
              </form>
            </div>
          </motion.header>
        )}
      </AnimatePresence>

      <main className="relative z-10">
        <AnimatePresence mode="wait">
          {status === "idle" && (
            <motion.div key="hero" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }}>
              <section className="mx-auto max-w-4xl px-6 pb-20 pt-20 sm:pt-28">
                <div className="text-center">
                  <motion.img
                    src="/logo.png"
                    alt="AskYeshua"
                    initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
                    className="mx-auto mb-6 h-20 w-20"
                  />
                  <motion.span
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 font-sans text-xs uppercase tracking-[0.2em] text-primary"
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Answers in Scripture
                  </motion.span>
                  <motion.h1
                    initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}
                    className="mt-8 font-serif text-5xl font-600 leading-[1.05] tracking-tight text-foreground sm:text-6xl lg:text-7xl"
                  >
                    Ask anything.
                    <br />
                    <span className="text-primary">Answered through the Bible.</span>
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
                    className="mx-auto mt-6 max-w-xl font-body text-lg leading-relaxed text-muted-foreground"
                  >
                    Every question meets a story that unfolds word by word, the very words of Scripture in the King James Version, and an illustration of the scene.
                  </motion.p>
                </div>

                <motion.form
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }}
                  onSubmit={(e) => { e.preventDefault(); ask(query); }}
                  className="mx-auto mt-12 flex max-w-2xl items-center gap-2 rounded-full border border-border bg-card p-2 pl-6 shadow-[0_8px_30px_rgba(140,106,71,0.08)] transition-colors focus-within:border-primary"
                >
                  <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
                  <input
                    data-testid="search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="What would you like to understand?"
                    className="w-full bg-transparent font-sans text-base outline-none placeholder:text-muted-foreground"
                  />
                  <Button data-testid="ask-button" type="submit" className="shrink-0 rounded-full px-6 py-6 font-sans">
                    Ask
                  </Button>
                </motion.form>

                <DailyVerse onExplore={ask} />

                <div className="mt-16">
                  <p className="mb-5 text-center font-sans text-xs uppercase tracking-[0.25em] text-muted-foreground">
                    Or begin with one of these
                  </p>
                  <div className="grid auto-rows-[130px] grid-cols-1 gap-4 md:grid-cols-3">
                    {EXAMPLE_QUESTIONS.map((ex, i) => (
                      <motion.button
                        key={ex.q}
                        data-testid="example-question-card"
                        onClick={() => ask(ex.q)}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + i * 0.08 }}
                        whileHover={{ y: -6 }}
                        className={`group relative flex flex-col justify-end overflow-hidden rounded-md border border-border bg-card p-5 text-left shadow-sm transition-shadow hover:shadow-lg ${ex.span}`}
                      >
                        {ex.image && (
                          <>
                            <SmartImage src={ex.image} alt="" className="absolute inset-0 h-full w-full" imgClassName="object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                          </>
                        )}
                        <div className="relative z-10">
                          <span className={`font-sans text-[0.65rem] uppercase tracking-[0.2em] ${ex.image ? "text-white/80" : "text-primary"}`}>
                            {ex.label}
                          </span>
                          <p className={`mt-1 font-serif text-xl leading-tight tracking-tight ${ex.image ? "text-white" : "text-foreground"}`}>
                            {ex.q}
                          </p>
                        </div>
                      </motion.button>
                    ))}
                  </div>

                  <div className="mt-6 flex flex-wrap justify-center gap-2">
                    {MORE_QUESTIONS.map((q) => (
                      <button
                        key={q}
                        data-testid="example-chip"
                        onClick={() => ask(q)}
                        className="rounded-full border border-border bg-card px-4 py-2 font-sans text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              </section>

              <Journeys onAsk={ask} />
              <Favorites />
              <Gallery />
            </motion.div>
          )}

          {(status === "streaming" || status === "answered") && answer && (
            <motion.div key="answer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {status === "streaming" && (
                <div className="mx-auto max-w-6xl px-6 pt-10 text-center">
                  <p className="font-sans text-sm uppercase tracking-[0.25em] text-primary">Searching the Scriptures...</p>
                </div>
              )}
              <AnswerView answer={answer} streaming={status === "streaming"} imageLoading={imageLoading} />
              {status === "answered" && (
                <div className="mx-auto max-w-6xl px-6 pb-24 text-center">
                  <Button data-testid="ask-another-button" onClick={reset} variant="outline" className="rounded-full font-sans">
                    <RotateCcw className="mr-2 h-4 w-4" /> Ask another question
                  </Button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <CrossBar className="pb-10 pt-6" />
    </div>
  );
}
